import classNames from 'classnames'
import { CSSProperties, useEffect } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { RichText, RichTextProps, Button } from '@src/quince-components/src'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import ButtonDomain from '@src/quince-site-common/src/domains/Button'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import Product from '@src/quince-site-common/src/domains/Product'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import makeChunks from '@src/quince-site-common/src/utils/makeChunks'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Swiper from '../../atoms/swiper'
import { DOTS_POSITION, NAV_POSITION } from '../../atoms/swiper/Swiper'
import { TrackConfig } from '../../hocs/withTracking'

import InfiniteScrollProductList from './infiniteScrollProductList'
import styles from './productListSectionTwo.module.scss'

interface ProductListSectionProps {
  heading?: string
  headingClassname?: string
  preHeadingRichTextProps?: RichTextProps
  headingRichTextProps?: RichTextProps
  subHeadingRichTextProps?: RichTextProps
  productCards: IProductCard[]
  wrapperClassName?: string
  productCardDesktopClassName?: string
  productCardMobileClassName?: string
  productCardWrapperMobileClassName?: string
  hideProductOptions?: boolean
  hideProductReviews?: boolean
  navPosition?: NAV_POSITION
  title?: string
  style?: CSSProperties
  layoutType?: LAYOUT_TYPES
  tracker?: string
  productClickTrackConfig?: TrackConfig
  numberOfSlidesToScroll?: number
  buttons?: ButtonDomain[]
  numberOfVisibleSlides?: number
  productCardExpClassName?: string
  chunkSize?: number
  infiniteScroll?: boolean
  showDots?: boolean
  wishlistIconOverrideClass?: string
  swiperClassName?: string
  hideWishlistIcon?: boolean
  hideProductName?: boolean
  gridColCount?: number
  isVertical?: boolean
  productPriceClass?: string
  skipTagRefresh?: boolean
  wishlistTrackConfig?: () => Record<string, any> | undefined
  additionalProductTrackingContext?: (pc: Product, idx: number) => Record<string, any> | undefined
  carouselArrowClickTracker?: ({ direction }: { direction: string }) => void
  productCardLinkClass?: string
  hideTags?: boolean
  productPriceWrapperClass?: string
  isNewDesign?: boolean
  productCardImageSectionClass?: string
  trackingMetaData?: TrackingMetaData
}

function ProductListSectionTwo({
  heading,
  headingClassname,
  preHeadingRichTextProps,
  headingRichTextProps,
  subHeadingRichTextProps,
  wrapperClassName,
  productCardDesktopClassName,
  productCardMobileClassName,
  productCardWrapperMobileClassName,
  hideProductOptions,
  hideProductReviews,
  layoutType = LAYOUT_TYPES.DEFAULT,
  title,
  style,
  tracker,
  productClickTrackConfig,
  productCards,
  buttons = [],
  productCardExpClassName,
  chunkSize = 4,
  infiniteScroll = false,
  showDots,
  wishlistIconOverrideClass,
  swiperClassName,
  hideWishlistIcon,
  hideProductName,
  gridColCount,
  isVertical = false,
  skipTagRefresh = false,
  wishlistTrackConfig = () => {
    return {}
  },
  additionalProductTrackingContext = () => {
    return {}
  },
  carouselArrowClickTracker,
  productPriceClass,
  productCardLinkClass,
  hideTags,
  isNewDesign,
  productCardImageSectionClass,
  trackingMetaData,
}: ProductListSectionProps) {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const productCardChunks = makeChunks(productCards, chunkSize)
  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: (isNewDesign ? styles.productCardWidthMobileNewDesign : styles.productCardWidthMobile) ?? '',
    maxWidthDesktopInRem: (isNewDesign ? styles.productCardWidthNewDesign : styles.productCardWidth) ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const _showDots = showDots ?? isMobile

  const DESKTOP_VISIBLE_SLIDES = isNewDesign ? 6 : 4

  const componentUniqueIdentifier = productClickTrackConfig?.context?.id as string
  const productViewTracker =
    (tracker && `${tracker}_${componentUniqueIdentifier}__productListSectionTwo`) ?? 'productListSectionTwo'

  useEffect(() => {
    if (productViewTracker && productClickTrackConfig?.context?.contentContainerType) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: function ({ events }) {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...productClickTrackConfig?.context,
          })
        },
      })
    }
  }, [productViewTracker, productClickTrackConfig?.context])

  return (
    <div
      id={title?.toLowerCase().split(' ').join('-')}
      className={classNames(styles.wrapper, styles[`wrapper__${layoutType}`], wrapperClassName)}
      style={style}
      data-testid={'productListSectionTwo'}
    >
      <div className={classNames(styles.textWrapper)}>
        {heading && <div className={headingClassname}>{heading}</div>}
        {preHeadingRichTextProps && (
          <RichText {...preHeadingRichTextProps} className={classNames(preHeadingRichTextProps.className)} />
        )}
        {headingRichTextProps && (
          <RichText {...headingRichTextProps} className={classNames(headingRichTextProps.className)} />
        )}
        {subHeadingRichTextProps && (
          <RichText {...subHeadingRichTextProps} className={classNames(subHeadingRichTextProps.className)} />
        )}
        {buttons.map(button => (
          <Button key={button.id} {...ButtonDomain.makeButtonProps(button)} />
        ))}
      </div>
      {!!productCards.length &&
        (infiniteScroll ? (
          <InfiniteScrollProductList
            productCards={productCards}
            swiperClassName={swiperClassName}
            gridColCount={gridColCount}
            tracker={tracker}
            productCardDesktopClassName={productCardDesktopClassName}
            productCardExpClassName={productCardExpClassName}
            productCardMobileClassName={productCardMobileClassName}
            wishlistIconOverrideClass={wishlistIconOverrideClass}
            maxWidth={maxWidth}
            hideReviews={hideProductReviews}
            hideOptions={hideProductOptions}
            hideWishlistIcon={hideWishlistIcon}
            productClickTrackConfig={productClickTrackConfig}
            optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
            hideProductName={hideProductName}
            productCardLinkClass={productCardLinkClass}
            hideTags={hideTags}
            skipTagRefresh={skipTagRefresh}
          />
        ) : (
          <Swiper
            className={classNames(styles.productList, swiperClassName)}
            numberOfVisibleSlides={isDesktop ? DESKTOP_VISIBLE_SLIDES : 1}
            showDots={_showDots}
            variableWidth
            showNavigator={isDesktop}
            dotsPosition={DOTS_POSITION.BOTTOM}
            tracker={tracker}
            id={tracker || 'productListSectionTwo'}
            isVertical={isVertical}
            arrowOnClick={carouselArrowClickTracker}
            trackingMetaData={trackingMetaData}
          >
            {isDesktop
              ? productCards.map((productCard, idx) => {
                  const initialSelectedVariant = getInitialSelectedVariant({
                    cardVariants: productCard.product.cardVariants,
                  })
                  const _tracker = tracker ? `${tracker}__0__${idx}` : undefined
                  return (
                    <ProductCard
                      key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                      className={classNames(
                        isNewDesign ? styles.productCardNewDesign : styles.productCard,
                        productCardDesktopClassName,
                      )}
                      productCard={productCard}
                      maxWidth={maxWidth}
                      hideReviews={hideProductReviews}
                      hideOptions={hideProductOptions}
                      tracker={_tracker}
                      productViewTracker={productViewTracker}
                      wishlistTrackConfig={wishlistTrackConfig(productCard, idx)}
                      skipTagRefresh={skipTagRefresh}
                      productClickTrackConfig={
                        productClickTrackConfig
                          ? {
                              ...productClickTrackConfig,
                              context: {
                                ...productClickTrackConfig.context,
                                ...additionalProductTrackingContext(productCard, idx),
                                row: 0,
                                column: idx,
                                productPosition: idx,
                                contentContainerType: productClickTrackConfig.context?.contentContainerType,
                                contentContainerName: productClickTrackConfig.context?.contentContainerName,
                                contentContainerDisplayName:
                                  productClickTrackConfig.context?.contentContainerDisplayName,
                              },
                            }
                          : undefined
                      }
                      shouldShowMore
                      hideTags={hideTags}
                      optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                      productPriceClass={productPriceClass}
                      hideWishlistIcon={hideWishlistIcon}
                      onShowMoreClick={({ selectedVariant }) => {
                        navigateToPDP({
                          slug: productCard.product.slug,
                          tracker: _tracker,
                          selectedOption: selectedVariant?.displayConfig.value,
                          router,
                        })
                      }}
                      productCardLinkClass={productCardLinkClass}
                      productCardImageSectionClass={productCardImageSectionClass}
                      showMScroll={false}
                    />
                  )
                })
              : productCardChunks.map((productChunk, i) => {
                  return (
                    <div
                      key={i}
                      className={classNames(
                        isNewDesign ? styles.productCardWrapperMobileNewDesign : styles.productCardWrapperMobile,
                        productCardWrapperMobileClassName,
                        i !== 0 && styles.productCardWrapperMobileChunks,
                      )}
                    >
                      {productChunk.map((productCard, j) => {
                        const initialSelectedVariant = getInitialSelectedVariant({
                          cardVariants: productCard.product.cardVariants,
                        })
                        const _tracker = tracker ? `${tracker}__${i}_${j}` : undefined
                        return (
                          <ProductCard
                            key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                            className={classNames(
                              isNewDesign ? styles.productCardNewDesign : styles.productCard,
                              productCardMobileClassName,
                            )}
                            wishlistIconClassName={classNames(
                              styles.productCard__wishlistIcon,
                              wishlistIconOverrideClass,
                            )}
                            productCard={productCard}
                            maxWidth={maxWidth}
                            hideReviews={hideProductReviews}
                            hideOptions={hideProductOptions}
                            tracker={_tracker}
                            productViewTracker={productViewTracker}
                            wishlistTrackConfig={wishlistTrackConfig(productCard, productCards.indexOf(productCard))}
                            skipTagRefresh={skipTagRefresh}
                            hideWishlistIcon={hideWishlistIcon}
                            productClickTrackConfig={
                              productClickTrackConfig
                                ? {
                                    ...productClickTrackConfig,
                                    context: {
                                      ...productClickTrackConfig.context,
                                      ...additionalProductTrackingContext(
                                        productCard,
                                        productCards.indexOf(productCard),
                                      ),
                                      row: i,
                                      column: j,
                                      contentContainerType: productClickTrackConfig.context?.contentContainerType,
                                      contentContainerName: productClickTrackConfig.context?.contentContainerName,
                                      contentContainerDisplayName:
                                        productClickTrackConfig.context?.contentContainerDisplayName,
                                    },
                                  }
                                : undefined
                            }
                            shouldShowMore
                            optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                            productPriceClass={productPriceClass}
                            onShowMoreClick={({ selectedVariant }) => {
                              navigateToPDP({
                                slug: productCard.product.slug,
                                tracker: _tracker,
                                selectedOption: selectedVariant?.displayConfig.value,
                                router,
                              })
                            }}
                            productCardLinkClass={productCardLinkClass}
                            hideTags={hideTags}
                            nameAndPriceSectionClass={isNewDesign ? styles.nameAndPriceSection : undefined}
                            productCardImageSectionClass={productCardImageSectionClass}
                            showMScroll={false}
                          />
                        )
                      })}
                    </div>
                  )
                })}
          </Swiper>
        ))}
    </div>
  )
}

export default ProductListSectionTwo
